// =============================================================================
// card-deck-manager.ts — pure-function port of Systems/CardDeckManager.cs
// No class, no singleton, no module-level state.
// Loads cards.json at module level (read-only, frozen). All deck state is
// threaded through functions as Readonly<Record<string, ReadonlyArray<CardDef>>>.
//
// Pool bucketing matches Unity (single source of truth — derived from cardType):
//   CardType.Event    → CardPool.Event
//   CardType.Policy / Tech / Mitigate → CardPool.Policy
// cardPool field has been removed from CardDef; pool is always derived.
// =============================================================================

import { CardsExportSchema } from "@gdg/shared";
import type { CardDef, CardsExport } from "@gdg/shared";
// Relative import — @/data alias not configured in rules-engine tsconfig.
// cards.json is at repo-root/data/cards.json; services/ is 4 dirs deep.
import cardsData from "../../../../data/cards.json";
import type { Rng } from "../rng.js";

// ---------------------------------------------------------------------------
// Typed errors
// ---------------------------------------------------------------------------

export class CardLoadError extends Error {
  constructor(
    message: string,
    public readonly code: "InvalidShape" | "EmptyDeck",
  ) {
    super(message);
    this.name = "CardLoadError";
  }
}

export class CardDrawError extends Error {
  constructor(
    message: string,
    public readonly code: "PoolExhausted" | "PoolNotFound",
  ) {
    super(message);
    this.name = "CardDrawError";
  }
}

// ---------------------------------------------------------------------------
// State types — all immutable
// ---------------------------------------------------------------------------

/**
 * Pool key values matching CardPool enum from shared/enums.ts.
 * Using string keys for frozen-record discipline.
 */
export type DeckPoolKey = "Event" | "Policy";

export interface DeckPoolState {
  /** draw pile — cards remaining to draw */
  readonly draw: ReadonlyArray<CardDef>;
  /** discard pile — awaiting reshuffle */
  readonly discard: ReadonlyArray<CardDef>;
}

export interface CardDeckState {
  readonly pools: Readonly<Record<DeckPoolKey, DeckPoolState>>;
}

// ---------------------------------------------------------------------------
// Load + validate cards.json (schema gate per Brief §3.3)
// ---------------------------------------------------------------------------

/**
 * Parses and validates cards.json against the shared Zod schema.
 * Throws CardLoadError if validation fails.
 * Result is frozen — callers must not mutate it.
 */
export function loadCards(): CardsExport {
  const result = CardsExportSchema.safeParse(cardsData);
  if (!result.success) {
    throw new CardLoadError(
      `cards.json failed schema validation: ${result.error.message}`,
      "InvalidShape",
    );
  }
  // Zod-validated data is structurally identical to CardsExport interface.
  const data = result.data as CardsExport;
  // Deep-freeze so tests cannot accidentally mutate the imported object.
  return deepFreezeCards(data);
}

function deepFreezeCards(data: CardsExport): CardsExport {
  for (const card of data.cards) {
    if (card.tags) Object.freeze(card.tags);
    for (const eff of card.effects) {
      if (eff.categoryFilter) Object.freeze(eff.categoryFilter);
      Object.freeze(eff);
    }
    Object.freeze(card.effects);
    Object.freeze(card);
  }
  Object.freeze(data.cards);
  Object.freeze(data._meta);
  return Object.freeze(data);
}

// ---------------------------------------------------------------------------
// Pool resolution: CardType → DeckPoolKey
// ---------------------------------------------------------------------------

/**
 * Derive pool from cardType only — mirrors Unity:
 *   public CardPool pool => cardType == CardType.Event ? CardPool.Event : CardPool.Policy;
 * cardPool field is intentionally ignored (removed from schema) to keep a
 * single source of truth and prevent silent bucketing divergence.
 * Exported for tests.
 */
export function derivePool(cardType: CardDef["cardType"]): DeckPoolKey {
  return cardType === "Event" ? "Event" : "Policy";
}

/** @internal used by initializeDeck + discardToPool */
function resolvePool(card: CardDef): DeckPoolKey {
  return derivePool(card.cardType);
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create initial empty deck state with both pools empty.
 */
export function createEmptyDeckState(): CardDeckState {
  const emptyPool: DeckPoolState = Object.freeze({
    draw: Object.freeze([]),
    discard: Object.freeze([]),
  });
  return Object.freeze({
    pools: Object.freeze({
      Event: emptyPool,
      Policy: emptyPool,
    }),
  });
}

/**
 * Initialize deck state from the validated card list.
 * Buckets each card into Event or Policy pool, then shuffles with the provided Rng.
 * Mirrors Unity CardDeckManager.InitializeDecks() + Shuffle.
 */
export function initializeDeck(cards: ReadonlyArray<CardDef>, rng: Rng): CardDeckState {
  const eventCards: CardDef[] = [];
  const policyCards: CardDef[] = [];

  for (const card of cards) {
    const pool = resolvePool(card);
    if (pool === "Event") {
      eventCards.push(card);
    } else {
      policyCards.push(card);
    }
  }

  const shuffledEvent = rng.shuffle(eventCards);
  const shuffledPolicy = rng.shuffle(policyCards);

  return Object.freeze({
    pools: Object.freeze({
      Event: Object.freeze({ draw: Object.freeze(shuffledEvent), discard: Object.freeze([]) }),
      Policy: Object.freeze({ draw: Object.freeze(shuffledPolicy), discard: Object.freeze([]) }),
    }),
  });
}

// ---------------------------------------------------------------------------
// Draw + discard
// ---------------------------------------------------------------------------

export interface DrawResult {
  readonly newState: CardDeckState;
  readonly card: CardDef;
  /** True when discard was reshuffled into draw before this draw. */
  readonly reshuffled: boolean;
}

/**
 * Draw the top card from a pool. If the draw pile is empty, reshuffles the
 * discard back into the draw (same as Unity DrawFromPool reshuffle path).
 * Throws CardDrawError when both draw and discard are exhausted.
 * Mirrors Unity CardDeckManager.DrawFromPool(pool).
 */
export function drawFromPool(state: CardDeckState, pool: DeckPoolKey, rng: Rng): DrawResult {
  const poolState: DeckPoolState = state.pools[pool];

  let draw = [...poolState.draw];
  let discard = [...poolState.discard];
  let reshuffled = false;

  if (draw.length === 0) {
    if (discard.length === 0) {
      throw new CardDrawError(
        `[CardDeck] ${pool} pool exhausted (deck and discard empty).`,
        "PoolExhausted",
      );
    }
    // Reshuffle discard back into draw.
    draw = rng.shuffle(discard);
    discard = [];
    reshuffled = true;
  }

  const card = draw[0];
  if (card === undefined) {
    // Defensive: should not happen given length check above.
    throw new CardDrawError(`[CardDeck] ${pool} draw pile unexpectedly empty.`, "PoolExhausted");
  }
  const newDraw = draw.slice(1);

  const newPools: Record<DeckPoolKey, DeckPoolState> = {
    ...state.pools,
    [pool]: Object.freeze({ draw: Object.freeze(newDraw), discard: Object.freeze(discard) }),
  };
  const newState: CardDeckState = Object.freeze({ pools: Object.freeze(newPools) });
  return { newState, card, reshuffled };
}

/**
 * Add a card to the discard pile of its pool.
 * Mirrors Unity CardDeckManager.DiscardToPool(card).
 */
export function discardToPool(state: CardDeckState, card: CardDef): CardDeckState {
  const pool = resolvePool(card);
  const poolState: DeckPoolState = state.pools[pool];
  const newDiscard = Object.freeze([...poolState.discard, card]);
  const newPools: Record<DeckPoolKey, DeckPoolState> = {
    ...state.pools,
    [pool]: Object.freeze({ draw: poolState.draw, discard: newDiscard }),
  };
  return Object.freeze({ pools: Object.freeze(newPools) });
}

/**
 * Count of cards in draw pile of a pool.
 * Mirrors Unity CountInPool(pool).
 */
export function countInPool(state: CardDeckState, pool: DeckPoolKey): number {
  return state.pools[pool].draw.length;
}

/**
 * Count of cards in discard pile of a pool.
 * Mirrors Unity CountInDiscard(pool).
 */
export function countInDiscard(state: CardDeckState, pool: DeckPoolKey): number {
  return state.pools[pool].discard.length;
}

/**
 * Find a card by cardName or asset name (name field).
 * Mirrors Unity CardDeckManager.FindCardByName(cardName).
 */
export function findCardByName(
  cards: ReadonlyArray<CardDef>,
  cardName: string,
): CardDef | undefined {
  if (!cardName) return undefined;
  return cards.find((c) => c.cardName === cardName || c.name === cardName);
}
